import { useEffect, useState } from 'react';
import { MECHANIC_CHECK_ITEMS, VEHICLE_CHECK_LABELS } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { formatDateShort } from '@/lib/format';
import { useCreateRepair, useMechanicCheck, useResolveCheck, useSetService } from '@/api/hooks/maintenance';
import type { MaintenanceVan, RepairPriority } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { DatePicker, Field, Input, Select, Textarea } from '@/components/ui/Field';
import { toast } from '@/stores/toast';

const errorText = (error: unknown) => (error instanceof Error ? error.message : undefined);

/** Бързи причини — майсторът пише рядко, натиска често. */
const SERVICE_REASONS = ['Планово обслужване', 'Ремонт', 'Гуми', 'Годишен технически преглед'];

/**
 * „В сервиз": причина и кога се очаква да е готов. Ако бусът има курсове в
 * този прозорец, сървърът ги връща — казваме ги веднага, диспечерът ще ги
 * премести.
 */
export function ServiceDialog({ van, onClose }: { van: MaintenanceVan | null; onClose: () => void }) {
  const setService = useSetService();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');

  useEffect(() => {
    setReason('');
    setUntil('');
  }, [van?.id]);

  if (!van) return null;
  const submit = () =>
    setService.mutate(
      {
        vehicleId: van.id,
        inService: true,
        reason: reason.trim(),
        until: until ? new Date(until).toISOString() : null,
      },
      {
        onSuccess: (result) => {
          if (result.conflicts.length > 0) {
            toast.warn(
              `Бус ${van.fleetNo} е в сервиз — има курсове за преместване`,
              result.conflicts
                .map((c) => `${c.number}${c.plannedStartAt ? ` от ${formatDateShort(c.plannedStartAt)}` : ''}`)
                .join(', '),
            );
          } else {
            toast.ok(`Бус ${van.fleetNo} е в сервиз`);
          }
          onClose();
        },
        onError: (error) => toast.error('Не стана', errorText(error)),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Бус ${van.fleetNo} в сервиз`}
      description="Докато е в сервиз, бусът не се назначава. Диспечерът вижда причината и кога ще е готов."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={setService.isPending} disabled={reason.trim().length < 3} onClick={submit}>
            В сервиз
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {SERVICE_REASONS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setReason(preset)}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs',
                reason === preset ? 'border-accent bg-accent-soft text-accent' : 'border-line-strong text-ink-dim hover:text-ink',
              )}
            >
              {preset}
            </button>
          ))}
        </div>
        <Field label="Защо" required>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Field label="Готов на (по желание)" hint="По него диспечерът планира следващия курс.">
          <DatePicker value={until} onChange={(e) => setUntil(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * Прегледът в сервиза: всяка точка — наред или не. Както при шофьора, всяка
 * точка трябва да е отговорена — „не съм гледал" не е „наред".
 */
export function CheckDialog({
  van,
  onClose,
  onProblems,
}: {
  van: MaintenanceVan | null;
  onClose: () => void;
  /** Нещо не е наред — предлагаме ремонт с готово описание. */
  onProblems: (van: MaintenanceVan, summary: string) => void;
}) {
  const check = useMechanicCheck();
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [odometer, setOdometer] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setAnswers({});
    setOdometer('');
    setNote('');
  }, [van?.id]);

  if (!van) return null;
  const answered = MECHANIC_CHECK_ITEMS.every((key) => answers[key] !== undefined);
  const failed = MECHANIC_CHECK_ITEMS.filter((key) => answers[key] === false);
  const km = Number(odometer.replace(/[^\d]/g, ''));

  const submit = () =>
    check.mutate(
      {
        vehicleId: van.id,
        items: MECHANIC_CHECK_ITEMS.map((key) => ({ key, ok: answers[key] === true })),
        ...(odometer && Number.isInteger(km) && km > 0 ? { odometerKm: km } : {}),
        ...(note.trim() ? { problem: note.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.ok(`Бус ${van.fleetNo} е проверен`, failed.length ? `Не е наред: ${failed.length}` : 'Всичко е наред');
          onClose();
          if (failed.length > 0 || note.trim()) {
            onProblems(
              van,
              [failed.map((key) => VEHICLE_CHECK_LABELS[key] ?? key).join(', '), note.trim()].filter(Boolean).join(' — '),
            );
          }
        },
        onError: (error) => toast.error('Проверката не е записана', errorText(error)),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      width="lg"
      title={`Проверка на бус ${van.fleetNo}`}
      description={`${van.plate}${van.model ? ` · ${van.model}` : ''}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={check.isPending} disabled={!answered} onClick={submit}>
            Запиши проверката
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <ul className="divide-y divide-line rounded-lg border border-line">
          {MECHANIC_CHECK_ITEMS.map((key) => (
            <li key={key} className="flex items-center gap-2 px-3 py-2">
              <span className="min-w-0 flex-1 text-sm">{VEHICLE_CHECK_LABELS[key] ?? key}</span>
              {[true, false].map((ok) => (
                <button
                  key={String(ok)}
                  type="button"
                  onClick={() => setAnswers({ ...answers, [key]: ok })}
                  className={cn(
                    'rounded border px-2.5 py-1 text-xs font-medium',
                    answers[key] === ok
                      ? ok
                        ? 'border-ok bg-ok-soft text-ok'
                        : 'border-danger bg-danger-soft text-danger'
                      : 'border-line-strong text-ink-dim hover:text-ink',
                  )}
                >
                  {ok ? 'наред' : 'не е наред'}
                </button>
              ))}
            </li>
          ))}
        </ul>
        <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
          <Field label="Километраж">
            <Input inputMode="numeric" value={odometer} onChange={(e) => setOdometer(e.target.value)} placeholder="например 184 532" />
          </Field>
          <Field label="Бележка">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="какво точно не е наред" />
          </Field>
        </div>
        {!answered ? <p className="text-xs text-ink-faint">Отговорете на всяка точка.</p> : null}
      </div>
    </Dialog>
  );
}

/** Нов ремонт — по бус, от сигнал или от прегледа. */
export function RepairCreateDialog({
  target,
  onClose,
  onCreated,
}: {
  target: { vehicleId: string; vehicleLabel: string; title?: string; description?: string; sourceCheckId?: string } | null;
  onClose: () => void;
  onCreated?: (repairId: string) => void;
}) {
  const create = useCreateRepair();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<RepairPriority>('normal');

  useEffect(() => {
    setTitle(target?.title ?? '');
    setDescription(target?.description ?? '');
    setPriority('normal');
  }, [target]);

  if (!target) return null;
  const submit = () =>
    create.mutate(
      {
        vehicleId: target.vehicleId,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        priority,
        ...(target.sourceCheckId ? { sourceCheckId: target.sourceCheckId } : {}),
      },
      {
        onSuccess: (repair) => {
          toast.ok('Ремонтът е заведен', repair.title);
          onClose();
          onCreated?.(repair.id);
        },
        onError: (error) => toast.error('Ремонтът не е заведен', errorText(error)),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Нов ремонт"
      description={target.vehicleLabel}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={create.isPending} disabled={title.trim().length < 3} onClick={submit}>
            Заведи
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Какво трябва да се оправи" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="например Смяна на предни накладки" />
        </Field>
        <Field label="Подробно">
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Спешност">
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value as RepairPriority)}
            options={[
              { value: 'urgent', label: 'Спешно — бусът не тръгва' },
              { value: 'normal', label: 'Нормално' },
              { value: 'low', label: 'При следващото минаване' },
            ]}
          />
        </Field>
      </div>
    </Dialog>
  );
}

const RESOLUTIONS = ['Проверено — наред', 'Оправено на място', 'Шофьорът е грешил'];

/** Сигналът от шофьора е прегледан — какво е направено. */
export function ResolveDialog({ checkId, onClose }: { checkId: string | null; onClose: () => void }) {
  const resolve = useResolveCheck();
  const [text, setText] = useState('');
  useEffect(() => setText(''), [checkId]);
  if (!checkId) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      title="Сигналът е прегледан"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button
            variant="primary"
            loading={resolve.isPending}
            disabled={text.trim().length < 3}
            onClick={() =>
              resolve.mutate(
                { checkId, resolution: text.trim() },
                {
                  onSuccess: () => {
                    toast.ok('Отбелязано');
                    onClose();
                  },
                  onError: (error) => toast.error('Не стана', errorText(error)),
                },
              )
            }
          >
            Запиши
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {RESOLUTIONS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setText(preset)}
              className="rounded-full border border-line-strong px-2 py-0.5 text-xs text-ink-dim hover:text-ink"
            >
              {preset}
            </button>
          ))}
        </div>
        <Field label="Какво е направено" required>
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
